import { Component, Input, OnInit, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { AppService } from 'src/@core/services/app.service';
import { ChatTakeoverState, JobService } from 'src/@core/services/job/job.service';
import { NotificationService } from 'src/@core/services/notification.service';
import { getWhatsAppDocumentsErrorMessage, openPresignedDownload } from 'src/@core/utils/presigned-download.util';

@Component({
  selector: 'hour4u-admin-activity-history',
  templateUrl: './activity-history.component.html',
  styleUrls: ['./activity-history.component.scss']
})
export class ActivityHistoryComponent implements OnInit, OnChanges, OnDestroy {
  @Input() candidateDetails: any;
  @Input() projectId: string;
  activityRecords: any[] = [];
  clientInfo: any = null;
  isDownloadingWaDocs = false;
  expandedChatRecords: { [key: string]: boolean } = {};
  expandedTranscriptRecords: { [key: string]: boolean } = {};
  copiedTranscriptKey: string | null = null;
  private copiedTranscriptTimer: any = null;
  readonly INITIAL_MESSAGE_LIMIT = 3;
  readonly EXPANDED_MAX_HEIGHT = 500;
  readonly INITIAL_TRANSCRIPT_LINE_LIMIT = 8;

  // For the call transcript UI we render it as chat-like rows.
  // Each row may map to a single "[ts] ROLE: text" line (plus any wrapped lines).
  readonly INITIAL_TRANSCRIPT_MESSAGE_LIMIT = 4;

  // Chat takeover: a person answers the lead instead of the AI, from the chat
  // panel of the lead's newest WhatsApp / website chat record.
  takeover: ChatTakeoverState | null = null;
  takeoverBusy = false;
  takeoverLoading = false;
  takeoverError = '';
  replyText = '';
  replySending = false;
  private takeoverLeadKey: string | null = null;
  private takeoverPollTimer: any = null;
  readonly TAKEOVER_POLL_MS = 5000;

  constructor(
    private appService: AppService,
    private jobService: JobService,
    private notify: NotificationService
  ) { }

  ngOnInit() {
    this.parseActivityHistory();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['candidateDetails'] && !changes['candidateDetails'].firstChange) {
      this.parseActivityHistory();
    }
  }

  ngOnDestroy() {
    this.stopTakeoverPoll();
    if (this.copiedTranscriptTimer) {
      clearTimeout(this.copiedTranscriptTimer);
      this.copiedTranscriptTimer = null;
    }
  }

  parseActivityHistory() {
    if (this.candidateDetails) {
      // Extract client info from first record if available
      if (this.candidateDetails.data && this.candidateDetails.data.length > 0) {
        const firstRecord = this.candidateDetails.data[0];
        this.clientInfo = {
          name: firstRecord.client_name || 'N/A',
          phone: firstRecord.client_number || 'N/A',
          email: firstRecord.client_email || ''
        };
      }

      // Parse activity records
      if (this.candidateDetails.data && Array.isArray(this.candidateDetails.data)) {
        let activityRecords = this.candidateDetails.data
          .map((record: any) => {
            const displayStatus = this.getDisplayStatus(record);
            const parsedRecord = {
              ...record,
              formattedDate: this.formatDate(record.start_time || record.recording_time),
              formattedDuration: this.formatDuration(record.duration),
              displayStatus,
              formattedStatus: this.formatStatus(displayStatus),
              chatMessages: [] as any[]
            };

            // Parse WhatsApp chat history for this record if it exists
            if (record.whatsapp_chat_history) {
              parsedRecord.chatMessages = this.parseWhatsAppChatHistoryForRecord(record.whatsapp_chat_history);
            }

            return parsedRecord;
          });

        this.activityRecords = activityRecords
          .sort((a: any, b: any) => {
            const timeA = a.start_time || a.recording_time || 0;
            const timeB = b.start_time || b.recording_time || 0;
            return timeB - timeA; // Sort descending (newest first)
          });
      }
    }
    this.loadTakeoverState();
  }

  // ── Chat takeover ─────────────────────────────────────────────────────────

  /** The record whose chat panel carries the takeover controls: the newest chat. */
  private getTakeoverRecord(): any {
    return this.activityRecords.find(r => r.conversation_type === 'whatsapp' && this.hasChatHistory(r)) || null;
  }

  isTakeoverRecord(record: any): boolean {
    const target = this.getTakeoverRecord();
    return !!target && this.getRecordKey(target) === this.getRecordKey(record);
  }

  private takeoverIds(): { entityId: string; projectId: string; peopleId: string } | null {
    const record = this.getTakeoverRecord();
    const account = this.appService.getCurrentAccount();
    const entityId = account && account.entity && account.entity.id;
    const projectId = (record && record.project_id) || this.projectId;
    const first = this.candidateDetails && this.candidateDetails.data && this.candidateDetails.data[0];
    const peopleId = (record && record.people_id) || (first && first.people_id);
    if (!entityId || !projectId || !peopleId) return null;
    return { entityId, projectId, peopleId };
  }

  private loadTakeoverState() {
    const ids = this.takeoverIds();
    if (!ids) {
      this.takeover = null;
      this.stopTakeoverPoll();
      return;
    }
    const leadKey = ids.projectId + ':' + ids.peopleId;
    if (leadKey === this.takeoverLeadKey) return; // already loaded for this lead
    this.takeoverLeadKey = leadKey;
    this.takeover = null;
    this.replyText = '';
    this.stopTakeoverPoll();
    this.fetchTakeoverState();
  }

  /** (Re)load the takeover state; a failure is shown with a retry, not hidden. */
  fetchTakeoverState() {
    const ids = this.takeoverIds();
    if (!ids || this.takeoverLoading) return;
    this.takeoverLoading = true;
    this.takeoverError = '';
    this.jobService.getChatTakeover(ids.entityId, ids.projectId, ids.peopleId).subscribe(
      (state) => {
        this.takeoverLoading = false;
        this.applyTakeoverState(state);
      },
      (err) => {
        this.takeoverLoading = false;
        this.takeover = null;
        this.takeoverError = this.apiError(err, 'Could not load chat takeover status.');
      }
    );
  }

  private applyTakeoverState(state: ChatTakeoverState) {
    this.takeover = state;
    const record = this.getTakeoverRecord();
    if (state && state.active) {
      // Show the whole conversation while a person is answering it.
      if (record) this.expandedChatRecords[this.getRecordKey(record)] = true;
      this.startTakeoverPoll();
    } else {
      this.stopTakeoverPoll();
    }
  }

  toggleTakeover() {
    const ids = this.takeoverIds();
    if (!ids || !this.takeover || this.takeoverBusy) return;
    const active = !this.takeover.active;
    this.takeoverBusy = true;
    this.jobService.setChatTakeover(ids.entityId, ids.projectId, ids.peopleId, active).subscribe(
      (state) => {
        this.takeoverBusy = false;
        this.applyTakeoverState(state);
        this.notify.showMessage(active
          ? 'You have taken over this chat. The AI will not reply until you hand it back.'
          : 'Chat handed back to the AI.', 4000);
      },
      (err) => {
        this.takeoverBusy = false;
        this.notify.showMessage(this.apiError(err, active ? 'Could not take over the chat.' : 'Could not hand the chat back.'), 6000);
      }
    );
  }

  onReplyKeydown(event: KeyboardEvent, record: any) {
    // Enter sends; Shift+Enter starts a new line.
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendReply(record);
    }
  }

  sendReply(record: any) {
    const ids = this.takeoverIds();
    const message = (this.replyText || '').trim();
    if (!ids || !message || this.replySending || !this.takeover || !this.takeover.active) return;
    this.replySending = true;
    this.jobService.sendChatHumanReply(ids.entityId, ids.projectId, ids.peopleId, message).subscribe(
      (res) => {
        this.replySending = false;
        this.replyText = '';
        const ts = (res && res.timestamp) || Math.floor(Date.now() / 1000);
        record.chatMessages = [...(record.chatMessages || []), {
          id: (res && res.message_id) || 'local-' + ts,
          role: 'assistant',
          content: message,
          timestamp: ts,
          formattedTime: this.formatTime(ts),
          formattedDateTime: this.formatMessageDateTime(ts),
          sentByHuman: true,
          sentByEmail: this.takeover ? this.takeover.by : ''
        }];
        this.expandedChatRecords[this.getRecordKey(record)] = true;
      },
      (err) => {
        this.replySending = false;
        this.notify.showMessage(this.apiError(err, 'Could not send the message.'), 6000);
      }
    );
  }

  /** Who sent a message a person wrote from the dashboard. */
  getHumanSenderLabel(message: any): string {
    const email = (message && message.sentByEmail) || '';
    return email ? 'Team · ' + email : 'Team member';
  }

  private startTakeoverPoll() {
    if (this.takeoverPollTimer) return;
    this.takeoverPollTimer = setInterval(() => this.refreshTakeoverChat(), this.TAKEOVER_POLL_MS);
  }

  private stopTakeoverPoll() {
    if (this.takeoverPollTimer) {
      clearInterval(this.takeoverPollTimer);
      this.takeoverPollTimer = null;
    }
  }

  /** Picks up the lead's new messages while a person is answering the chat. */
  private refreshTakeoverChat() {
    const record = this.getTakeoverRecord();
    const ids = this.takeoverIds();
    if (!record || !ids || !record.client_number || this.replySending) return;
    this.jobService.getJobsStatusByClient(ids.peopleId, ids.projectId, record.client_number).subscribe(
      (res) => {
        const rows = (res && res.data) || [];
        const fresh = rows.find((r: any) => r.job_id && r.job_id === record.job_id)
          || rows.find((r: any) => r.conversation_type === 'whatsapp');
        if (!fresh || !fresh.whatsapp_chat_history) return;
        const messages = this.parseWhatsAppChatHistoryForRecord(fresh.whatsapp_chat_history);
        if (messages.length >= (record.chatMessages || []).length) {
          record.chatMessages = messages;
        }
      },
      () => { /* keep what is shown; the next poll retries */ }
    );
  }

  private apiError(err: any, fallback: string): string {
    const body = err && err.error;
    return (body && (body.error || body.message)) || fallback;
  }

  formatDate(timestamp: string | number): string {
    if (!timestamp) return '';

    const date = new Date(typeof timestamp === 'string' ? parseFloat(timestamp) * 1000 : timestamp * 1000);
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    };

    return date.toLocaleDateString('en-US', options);
  }

  formatDuration(duration: string | number): string {
    if (!duration) return '0s';

    const seconds = typeof duration === 'string' ? parseFloat(duration) : duration;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);

    if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    }
    return `${remainingSeconds}s`;
  }

  // Depending on where the activity came from the status lives under a different
  // key, so fall back through them in order of preference.
  getDisplayStatus(record: any): string {
    if (!record) return '';

    const candidates = [record.current_status, record.interaction_status, record.status];
    for (const candidate of candidates) {
      if (typeof candidate === 'string' && candidate.trim()) {
        return candidate.trim();
      }
    }
    return '';
  }

  formatStatus(status: string): string {
    if (!status) return '';
    return status.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  }

  getStatusBadgeClass(status: string): string {
    if (!status) return 'status-badge';

    const statusLower = status.toLowerCase();
    if (statusLower.includes('scheduled') || statusLower.includes('meeting')) {
      return 'status-badge status-success';
    } else if (statusLower.includes('interested') || statusLower.includes('warm')) {
      return 'status-badge status-info';
    } else if (statusLower.includes('not interested') || statusLower.includes('cold')) {
      return 'status-badge status-warning';
    }
    return 'status-badge';
  }

  hasQuestions(record: any): boolean {
    return record.questions_asked && Array.isArray(record.questions_asked) && record.questions_asked.length > 0;
  }

  hasProficiencyScore(record: any): boolean {
    return record.proficiency_score && record.proficiency_score.overall_score !== undefined;
  }

  getProficiencyScore(record: any): number {
    if (this.hasProficiencyScore(record)) {
      return Math.round(record.proficiency_score.overall_score * 10) / 10;
    }
    return 0;
  }

  parseWhatsAppChatHistoryForRecord(whatsappChatHistory: string | any): any[] {
    if (!whatsappChatHistory) {
      return [];
    }

    try {
      // Parse JSON string if it's a string
      let chatData: any;
      if (typeof whatsappChatHistory === 'string') {
        chatData = JSON.parse(whatsappChatHistory);
      } else {
        chatData = whatsappChatHistory;
      }

      // Extract messages from history
      const messagesObj = (chatData && chatData.chat && chatData.chat.history && chatData.chat.history.messages) ||
        (chatData && chatData.history && chatData.history.messages) ||
        {};

      if (Object.keys(messagesObj).length === 0) {
        return [];
      }

      // Convert messages object to array and sort by timestamp
      const messagesArray: any[] = Object.values(messagesObj).map((msg: any) => ({
        id: msg.id,
        role: msg.role,
        content: msg.content || '',
        timestamp: msg.timestamp || 0,
        formattedTime: this.formatTime(msg.timestamp),
        formattedDateTime: this.formatMessageDateTime(msg.timestamp),
        whatsapp_media_s3_url: msg.whatsapp_media_s3_url || '',
        whatsapp_media_s3_key: msg.whatsapp_media_s3_key || '',
        whatsapp_media_kind: msg.whatsapp_media_kind || '',
        whatsapp_media_content_type: msg.whatsapp_media_content_type || '',
        // A reply a person wrote from the dashboard while the chat was taken over.
        sentByHuman: msg.sent_by === 'human',
        sentByEmail: msg.sent_by_email || ''
      }));

      // Filter out system messages and sort by timestamp
      return messagesArray
        .filter(msg => msg.role !== 'system')
        .sort((a, b) => a.timestamp - b.timestamp);

    } catch (error) {
      console.error('Error parsing WhatsApp chat history:', error);
      return [];
    }
  }

  hasChatHistory(record: any): boolean {
    return record.chatMessages && record.chatMessages.length > 0;
  }

  hasCallTranscript(record: any): boolean {
    const t = record && (record.call_transcript ? record.call_transcript : record.callTranscript);
    return !!(typeof t === 'string' ? t.trim() : t);
  }

  getCallTranscriptText(record: any): string {
    const t = record && (record.call_transcript ? record.call_transcript : record.callTranscript);
    if (!t) return '';
    if (typeof t === 'string') return t;
    try {
      return JSON.stringify(t, null, 2);
    } catch {
      return String(t);
    }
  }

  // The API returns call transcripts in two shapes:
  //   1) "[2026-07-29T15:36:55.698+00:00] ASSISTANT: text"  (timestamped)
  //   2) "assistant: text"                                   (role only, no timestamp)
  // Both are normalised to the same message list so the UI renders them identically.
  getCallTranscriptMessages(record: any): Array<{ ts?: string; tsLabel?: string; role: 'assistant' | 'user' | 'unknown'; text: string }> {
    const raw = this.getCallTranscriptText(record) || '';
    const lines = raw.split(/\r?\n/).map(l => (l || '').trim()).filter(l => l.length > 0);
    if (!lines.length) return [];

    const out: Array<{ ts?: string; tsLabel?: string; role: 'assistant' | 'user' | 'unknown'; text: string }> = [];
    const timestampedRe = /^\[(.+?)\]\s*([A-Za-z][A-Za-z _-]*?)\s*:\s*([\s\S]*)$/;
    const plainRe = /^([A-Za-z][A-Za-z _-]*?)\s*:\s*([\s\S]*)$/;

    for (const line of lines) {
      const timestamped = timestampedRe.exec(line);
      if (timestamped) {
        const ts = timestamped[1];
        out.push({
          ts,
          tsLabel: this.formatTranscriptTimestamp(ts),
          role: this.normalizeTranscriptRole(timestamped[2]),
          text: (timestamped[3] || '').trim()
        });
        continue;
      }

      // "assistant: ..." / "user: ..." with no timestamp. Only treat the prefix as a
      // speaker when it is a role we recognise, so ordinary text such as
      // "Note: call back later" stays part of the previous message.
      const plain = plainRe.exec(line);
      if (plain && this.normalizeTranscriptRole(plain[1]) !== 'unknown') {
        out.push({
          role: this.normalizeTranscriptRole(plain[1]),
          text: (plain[2] || '').trim()
        });
        continue;
      }

      // Continuation of previous message, or unknown format.
      if (out.length) {
        out[out.length - 1].text = (out[out.length - 1].text + '\n' + line).trim();
      } else {
        out.push({ role: 'unknown', text: line });
      }
    }

    return out;
  }

  private normalizeTranscriptRole(roleRaw: string): 'assistant' | 'user' | 'unknown' {
    const role = (roleRaw || '').trim().toLowerCase().replace(/[_-]+/g, ' ');
    if (['assistant', 'agent', 'ai', 'bot', 'system'].indexOf(role) !== -1) {
      return 'assistant';
    }
    if (['user', 'customer', 'client', 'caller', 'human', 'lead'].indexOf(role) !== -1) {
      return 'user';
    }
    return 'unknown';
  }

  private formatTranscriptTimestamp(ts: string): string {
    if (!ts) return '';

    const date = new Date(ts);
    if (isNaN(date.getTime())) return ts;

    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  }

  getTranscriptSpeakerLabel(msg: { role: string }): string {
    if (msg.role === 'assistant') return 'Agent';
    if (msg.role === 'user') return this.getClientDisplayName();
    return 'Transcript';
  }

  getClientDisplayName(): string {
    if (this.clientInfo && this.clientInfo.name && this.clientInfo.name !== 'N/A') {
      return this.clientInfo.name;
    }
    return 'Customer';
  }

  getVisibleTranscriptMessages(record: any): Array<{ ts?: string; tsLabel?: string; role: 'assistant' | 'user' | 'unknown'; text: string }> {
    const msgs = this.getCallTranscriptMessages(record);
    if (this.isTranscriptExpanded(record)) return msgs;
    return msgs.slice(0, this.INITIAL_TRANSCRIPT_MESSAGE_LIMIT);
  }

  private getRecordKey(record: any): string {
    return record.job_id || record.id || JSON.stringify(record);
  }

  formatTime(timestamp: number): string {
    if (!timestamp) return '';

    const date = new Date(timestamp * 1000);
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    const displayMinutes = minutes.toString().padStart(2, '0');

    return `${displayHours}:${displayMinutes} ${ampm}`;
  }

  formatMessageDateTime(timestamp: number): string {
    if (!timestamp) return '';

    const date = new Date(timestamp * 1000);
    const day = date.getDate();
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();
    const currentYear = new Date().getFullYear();
    const datePart = year === currentYear ? `${day} ${month}` : `${day} ${month} ${year}`;

    return `${datePart}, ${this.formatTime(timestamp)}`;
  }

  formatMessageContent(content: string): string {
    if (!content) return '';

    // Convert markdown-style formatting to HTML
    let formatted = content
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') // Bold
      .replace(/\*(.*?)\*/g, '<em>$1</em>') // Italic
      .replace(/\n/g, '<br>'); // Line breaks

    return formatted;
  }

  isUserMessage(role: string): boolean {
    return role === 'user';
  }

  isAssistantMessage(role: string): boolean {
    return role === 'assistant';
  }

  getWhatsAppMediaUrl(message: any): string {
    return message && message.whatsapp_media_s3_url ? message.whatsapp_media_s3_url : '';
  }

  hasWhatsAppImage(message: any): boolean {
    if (!message || !message.whatsapp_media_s3_url) return false;
    const kind = (message.whatsapp_media_kind || '').toLowerCase();
    const contentType = (message.whatsapp_media_content_type || '').toLowerCase();
    return kind === 'image' || contentType.startsWith('image/');
  }

  hasWhatsAppPdf(message: any): boolean {
    if (!message || !message.whatsapp_media_s3_url) return false;
    const kind = (message.whatsapp_media_kind || '').toLowerCase();
    const contentType = (message.whatsapp_media_content_type || '').toLowerCase();
    if (kind === 'document' || kind === 'pdf') return true;
    if (contentType === 'application/pdf') return true;

    const key = (message.whatsapp_media_s3_key || '').toLowerCase();
    const url = message.whatsapp_media_s3_url.toLowerCase();
    return key.endsWith('.pdf') || /\.pdf(\?|$)/.test(url);
  }

  getWhatsAppImageUrl(message: any): string {
    return this.hasWhatsAppImage(message) ? this.getWhatsAppMediaUrl(message) : '';
  }

  getWhatsAppMediaFileName(message: any): string {
    const key = message && message.whatsapp_media_s3_key;
    if (!key) return 'Document.pdf';

    const fileName = key.split('/').pop();
    return fileName || 'Document.pdf';
  }

  openWhatsAppMedia(url: string): void {
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }

  isWhatsAppMediaPlaceholder(content: string): boolean {
    if (!content) return false;
    return /^\[User sent a WhatsApp .+?\.\.\.\]$/i.test(content.trim());
  }

  shouldShowMessageText(message: any): boolean {
    if (!message || !message.content) return false;
    if ((this.hasWhatsAppImage(message) || this.hasWhatsAppPdf(message))
      && this.isWhatsAppMediaPlaceholder(message.content)) {
      return false;
    }
    return true;
  }

  isMediaOnlyMessage(message: any): boolean {
    return (this.hasWhatsAppImage(message) || this.hasWhatsAppPdf(message))
      && !this.shouldShowMessageText(message);
  }

  getUserInitial(): string {
    if (this.clientInfo && this.clientInfo.name) {
      const firstName = this.clientInfo.name.split(' ')[0];
      return firstName.charAt(0).toUpperCase();
    }
    return 'U';
  }

  shouldShowMore(record: any): boolean {
    return record.chatMessages && record.chatMessages.length > this.INITIAL_MESSAGE_LIMIT && !this.isChatExpanded(record);
  }

  isChatExpanded(record: any): boolean {
    const recordId = this.getRecordKey(record);
    return this.expandedChatRecords[recordId] || false;
  }

  toggleChatExpansion(record: any): void {
    const recordId = this.getRecordKey(record);
    this.expandedChatRecords[recordId] = !this.expandedChatRecords[recordId];
  }

  getVisibleMessages(record: any): any[] {
    if (!record.chatMessages) return [];
    if (this.isChatExpanded(record)) {
      return record.chatMessages;
    }
    return record.chatMessages.slice(0, this.INITIAL_MESSAGE_LIMIT);
  }

  getChatContainerHeight(record: any): string {
    if (this.isChatExpanded(record)) {
      return this.EXPANDED_MAX_HEIGHT + 'px';
    }
    return '300px';
  }

  isTranscriptExpanded(record: any): boolean {
    const recordId = this.getRecordKey(record);
    return this.expandedTranscriptRecords[recordId] || false;
  }

  toggleTranscriptExpansion(record: any): void {
    const recordId = this.getRecordKey(record);
    this.expandedTranscriptRecords[recordId] = !this.expandedTranscriptRecords[recordId];
  }

  shouldShowMoreTranscript(record: any): boolean {
    const msgs = this.getCallTranscriptMessages(record);
    return msgs.length > this.INITIAL_TRANSCRIPT_MESSAGE_LIMIT;
  }

  getTranscriptContainerHeight(record: any): string {
    if (this.isTranscriptExpanded(record)) {
      return this.EXPANDED_MAX_HEIGHT + 'px';
    }
    return '260px';
  }

  getTranscriptMessageCount(record: any): number {
    return this.getCallTranscriptMessages(record).length;
  }

  isTranscriptCopied(record: any): boolean {
    return this.copiedTranscriptKey === this.getRecordKey(record);
  }

  // Builds a clean, shareable version of the transcript regardless of which of the
  // two API shapes it arrived in.
  buildTranscriptForCopy(record: any): string {
    const messages = this.getCallTranscriptMessages(record);
    if (!messages.length) return '';

    const headerBits: string[] = [`Call transcript — ${this.getClientDisplayName()}`];
    const metaBits: string[] = [];
    if (record.formattedDate) metaBits.push(record.formattedDate);
    if (record.formattedDuration) metaBits.push(record.formattedDuration);
    if (record.formattedStatus) metaBits.push(record.formattedStatus);
    if (metaBits.length) headerBits.push(metaBits.join(' · '));

    const body = messages.map(msg => {
      const speaker = this.getTranscriptSpeakerLabel(msg);
      const time = msg.tsLabel ? `[${msg.tsLabel}] ` : '';
      return `${time}${speaker}: ${msg.text}`;
    });

    return `${headerBits.join('\n')}\n${'—'.repeat(32)}\n${body.join('\n')}\n`;
  }

  copyTranscript(record: any): void {
    const text = this.buildTranscriptForCopy(record);
    if (!text) {
      this.notify.showMessage('There is no transcript to copy.', 3000);
      return;
    }

    this.writeToClipboard(text).then(copied => {
      if (!copied) {
        this.notify.showMessage('Could not copy the transcript.', 3000);
        return;
      }

      const recordKey = this.getRecordKey(record);
      this.copiedTranscriptKey = recordKey;
      if (this.copiedTranscriptTimer) {
        clearTimeout(this.copiedTranscriptTimer);
      }
      this.copiedTranscriptTimer = setTimeout(() => {
        if (this.copiedTranscriptKey === recordKey) {
          this.copiedTranscriptKey = null;
        }
        this.copiedTranscriptTimer = null;
      }, 2200);
    });
  }

  private writeToClipboard(text: string): Promise<boolean> {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      return navigator.clipboard.writeText(text)
        .then(() => true)
        .catch(() => this.legacyCopy(text));
    }
    return Promise.resolve(this.legacyCopy(text));
  }

  private legacyCopy(text: string): boolean {
    try {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      const succeeded = document.execCommand('copy');
      document.body.removeChild(textarea);
      return succeeded;
    } catch {
      return false;
    }
  }

  getResolvedProjectId(): string {
    if (this.projectId) {
      return this.projectId;
    }
    if (this.candidateDetails && this.candidateDetails.project_id) {
      return this.candidateDetails.project_id;
    }
    if (this.activityRecords.length && this.activityRecords[0].project_id) {
      return this.activityRecords[0].project_id;
    }
    return '';
  }

  getResolvedClientNumber(): string {
    if (this.clientInfo && this.clientInfo.phone && this.clientInfo.phone !== 'N/A') {
      return this.clientInfo.phone;
    }
    if (this.candidateDetails && this.candidateDetails.client_number) {
      return this.candidateDetails.client_number;
    }
    if (this.activityRecords.length && this.activityRecords[0].client_number) {
      return this.activityRecords[0].client_number;
    }
    return '';
  }

  canDownloadWhatsAppDocuments(): boolean {
    const clientNumber = this.getResolvedClientNumber();
    return !!(this.getResolvedProjectId() && clientNumber && clientNumber !== 'Reveal');
  }

  downloadWhatsAppDocuments(): void {
    const projectId = this.getResolvedProjectId();
    const clientNumber = this.getResolvedClientNumber();

    if (!projectId) {
      this.notify.showMessage('Project ID is missing', 3000);
      return;
    }
    if (!clientNumber || clientNumber === 'Reveal') {
      this.notify.showMessage('Client phone number is not available for download.', 4000);
      return;
    }
    if (this.isDownloadingWaDocs) {
      return;
    }

    this.isDownloadingWaDocs = true;
    this.notify.showMessage('Preparing WhatsApp documents zip…', 3000);

    this.jobService.downloadProjectWhatsAppDocuments(projectId, clientNumber).subscribe({
      next: (response) => {
        this.isDownloadingWaDocs = false;
        if (!response || !response.downloadUrl) {
          this.notify.showMessage('Download URL was not returned.', 4000);
          return;
        }
        openPresignedDownload(
          response.downloadUrl,
          response.zipFilename || `${clientNumber}_documents.zip`
        );
        const count = response.fileCount != null ? response.fileCount : '';
        this.notify.showMessage(
          count !== '' ? `Download started (${count} file${count === 1 ? '' : 's'}).` : 'Download started.',
          3000
        );
      },
      error: (err) => {
        this.isDownloadingWaDocs = false;
        this.notify.showMessage(getWhatsAppDocumentsErrorMessage(err), 5000);
      }
    });
  }
}
